import { useEffect, useState, useMemo } from 'react';
import { continueRender, delayRender, useCurrentFrame } from 'remotion';
import { Page, RemotionConfig } from '@goldcast/player-config/src/types/remotion-types';
import { approxGTE, approxLTE } from '@goldcast/player-config/src/utils/compareUtils';
import type { Font } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Font';
import { CAPTION_PAGES_FRAME_BUFFER } from '@goldcast/player-config/src/constants/Remotion.constants';
import CaptionsPage from './CaptionsPage';
import { getComputedCaptionsStyles } from './utils';
import appLogger from '@/services/logger';
import { loadRemotionFont } from '@/libs/fonts';

export default function CaptionsPages({
  pages,
  onOutlineRelease,
  drift,
  canEditCaptions,
  font,
  disableCaptionsDragging = false,
  disableCaptionsResize = false,
  onOutlineResize
}: Pick<RemotionConfig, 'onOutlineRelease' | 'drift' | 'canEditCaptions' | 'onOutlineResize'> & {
  pages: Page[];
  font?: Font;
  disableCaptionsDragging?: boolean;
  disableCaptionsResize?: boolean;
}) {
  const [handle] = useState(() => delayRender('Loading Assets...', { retries: 3 }));
  const frame = useCurrentFrame();

  const visiblePages = useMemo(() => {
    return pages.filter(page => {
      const pageStart = page.fromFrame - CAPTION_PAGES_FRAME_BUFFER;
      const pageEnd = page.toFrame + CAPTION_PAGES_FRAME_BUFFER;
      const currentFrame = frame - drift;

      return approxGTE(currentFrame, pageStart) && approxLTE(currentFrame, pageEnd) && !page.isHidden;
    });
  }, [pages, frame, drift]);

  const hasPages = useMemo(() => pages.length > 0, [pages.length]);

  const computedCaptionsStyles = useMemo(() => {
    return hasPages ? getComputedCaptionsStyles(pages[0]) : { groupedTokensStyle: {} };
  }, [hasPages, pages]);

  useEffect(() => {
    if (font?.location && font.name) {
      loadRemotionFont(font)
        .then(() => {
          // Font loaded successfully
        })
        .catch(error => {
          appLogger.error('Error loading font in CaptionsPages', { error });
        })
        .finally(() => {
          continueRender(handle);
        });
    } else {
      continueRender(handle);
    }
  }, [font?.location, font?.name]);

  return visiblePages.map(page => (
    <CaptionsPage
      key={`${page.fromFrame}-${page.toFrame}`}
      page={page}
      onOutlineRelease={onOutlineRelease}
      canEditCaptions={canEditCaptions}
      drift={drift}
      disableCaptionsDragging={disableCaptionsDragging}
      disableCaptionsResize={disableCaptionsResize}
      onOutlineResize={onOutlineResize}
      computedCaptionsStyles={computedCaptionsStyles}
    />
  ));
}
